import { useState, useEffect } from 'react'
import { Plus, Edit, Trash2, UserCircle, Mail, Key, Building2 } from 'lucide-react'
import {
  usersService,
  type CreateUserData,
  type UserWithRelations,
} from '@/services/api/users.service'
import { doctorsService } from '@/services/api/doctors.service'
import { clinicsService } from '@/services/api/clinics.service'
import { patientsService } from '@/services/api/patients.service'
import type { UserRole, Doctor, Clinic, Patient } from '@/types/models'
import { toast } from 'react-hot-toast'

const AVAILABLE_ROLES: { value: UserRole; label: string; color: string }[] = [
  { value: 'Administrador', label: 'Administrador', color: 'bg-purple-100 text-purple-800' },
  { value: 'Médico', label: 'Médico', color: 'bg-blue-100 text-blue-800' },
  { value: 'Recepcionista', label: 'Recepcionista', color: 'bg-green-100 text-green-800' },
  { value: 'Paciente', label: 'Paciente', color: 'bg-pink-100 text-pink-800' },
]

export default function UsersPage() {
  const [users, setUsers] = useState<UserWithRelations[]>([])
  const [isLoading, setIsLoading] = useState(true)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserWithRelations | null>(null)
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false)
  const [userToDelete, setUserToDelete] = useState<UserWithRelations | null>(null)
  const [isClinicsModalOpen, setIsClinicsModalOpen] = useState(false)
  const [temporaryPassword, setTemporaryPassword] = useState<string | null>(null)
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false)

  // Pagination
  const [currentPage, setCurrentPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [total, setTotal] = useState(0)
  const limit = 10

  useEffect(() => {
    loadUsers()
  }, [currentPage])

  const loadUsers = async () => {
    try {
      setIsLoading(true)
      const response = await usersService.getAll({
        page: currentPage,
        limit,
        sortBy: 'name',
        sortOrder: 'asc',
      })
      setUsers(response.data)
      setTotalPages(response.pagination.totalPages)
      setTotal(response.pagination.total)
    } catch (error) {
      console.error('Error loading users:', error)
      toast.error('Erro ao carregar usuários')
    } finally {
      setIsLoading(false)
    }
  }

  const handleCreate = () => {
    setSelectedUser(null)
    setIsModalOpen(true)
  }

  const handleEdit = (user: UserWithRelations) => {
    setSelectedUser(user)
    setIsModalOpen(true)
  }

  const handleDelete = (user: UserWithRelations) => {
    setUserToDelete(user)
    setIsDeleteDialogOpen(true)
  }

  const confirmDelete = async () => {
    if (!userToDelete) return

    try {
      await usersService.delete(userToDelete.id)
      toast.success('Usuário excluído com sucesso!')
      loadUsers()
      setIsDeleteDialogOpen(false)
      setUserToDelete(null)
    } catch (error) {
      console.error('Error deleting user:', error)
      toast.error('Erro ao excluir usuário')
    }
  }

  const handleToggleStatus = async (user: UserWithRelations) => {
    try {
      await usersService.toggleStatus(user.id)
      toast.success(`Usuário ${user.isActive ? 'desativado' : 'ativado'} com sucesso!`)
      loadUsers()
    } catch (error) {
      console.error('Error toggling user status:', error)
      toast.error('Erro ao alterar status do usuário')
    }
  }

  const handleResetPassword = async (user: UserWithRelations) => {
    if (!confirm(`Deseja realmente resetar a senha de ${user.name || user.email}?`)) return

    try {
      const response = await usersService.resetPassword(user.id)
      setTemporaryPassword(response.temporaryPassword)
      setIsPasswordModalOpen(true)
      toast.success('Senha resetada com sucesso!')
      loadUsers()
    } catch (error) {
      console.error('Error resetting password:', error)
      toast.error('Erro ao resetar senha')
    }
  }

  const handleManageClinics = (user: UserWithRelations) => {
    setSelectedUser(user)
    setIsClinicsModalOpen(true)
  }

  const getRoleBadgeColor = (role: UserRole) => {
    return AVAILABLE_ROLES.find((r) => r.value === role)?.color || 'bg-gray-100 text-gray-800'
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-gray-500">Carregando...</div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciar Usuários</h1>
          <p className="mt-1 text-sm text-gray-500">
            {total} {total === 1 ? 'usuário cadastrado' : 'usuários cadastrados'}
          </p>
        </div>
        <button
          onClick={handleCreate}
          className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          <Plus size={20} />
          Novo Usuário
        </button>
      </div>

      {/* Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Usuário
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Roles
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                Status
              </th>
              <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                Ações
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {users.map((user) => (
              <tr key={user.id} className="hover:bg-gray-50">
                <td className="px-6 py-4">
                  <div className="flex items-center">
                    <UserCircle className="text-gray-400 mr-3 flex-shrink-0" size={20} />
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {user.name || 'Sem nome'}
                      </div>
                      <div className="flex items-center text-sm text-gray-500">
                        <Mail className="mr-1" size={12} />
                        {user.email}
                      </div>
                    </div>
                  </div>
                </td>
                <td className="px-6 py-4">
                  {user.role ? (
                    <span
                      className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getRoleBadgeColor(
                        user.role
                      )}`}
                    >
                      {user.role}
                    </span>
                  ) : (
                    <span className="text-sm text-gray-400">Sem role</span>
                  )}
                </td>
                <td className="px-6 py-4 whitespace-nowrap">
                  <button
                    onClick={() => handleToggleStatus(user)}
                    className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                      user.isActive ? 'bg-green-100 text-green-800' : 'bg-gray-100 text-gray-800'
                    }`}
                  >
                    {user.isActive ? 'Ativo' : 'Inativo'}
                  </button>
                </td>
                <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                  <div className="flex items-center justify-end gap-2">
                    <button
                      onClick={() => handleManageClinics(user)}
                      className="text-purple-600 hover:text-purple-900"
                      title="Gerenciar Clínicas"
                    >
                      <Building2 size={18} />
                    </button>
                    <button
                      onClick={() => handleResetPassword(user)}
                      className="text-amber-600 hover:text-amber-900"
                      title="Resetar Senha"
                    >
                      <Key size={18} />
                    </button>
                    <button
                      onClick={() => handleEdit(user)}
                      className="text-blue-600 hover:text-blue-900"
                      title="Editar"
                    >
                      <Edit size={18} />
                    </button>
                    <button
                      onClick={() => handleDelete(user)}
                      className="text-red-600 hover:text-red-900"
                      title="Excluir"
                    >
                      <Trash2 size={18} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {users.length === 0 && (
          <div className="text-center py-12">
            <UserCircle className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900">Nenhum usuário cadastrado</h3>
            <p className="mt-1 text-sm text-gray-500">Comece criando um novo usuário.</p>
            <div className="mt-6">
              <button
                onClick={handleCreate}
                className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                <Plus size={20} />
                Novo Usuário
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-700">
            Página {currentPage} de {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Anterior
            </button>
            <button
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              className="px-4 py-2 border border-gray-300 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Próxima
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      {isModalOpen && (
        <UserFormModal
          user={selectedUser}
          onClose={() => setIsModalOpen(false)}
          onSuccess={(tempPassword) => {
            loadUsers()
            setIsModalOpen(false)
            if (tempPassword) {
              setTemporaryPassword(tempPassword)
              setIsPasswordModalOpen(true)
            }
          }}
        />
      )}

      {isClinicsModalOpen && selectedUser && (
        <ManageClinicsModal
          user={selectedUser}
          onClose={() => {
            setIsClinicsModalOpen(false)
            setSelectedUser(null)
          }}
          onSuccess={() => {
            loadUsers()
          }}
        />
      )}

      {isPasswordModalOpen && temporaryPassword && (
        <TemporaryPasswordModal
          password={temporaryPassword}
          onClose={() => {
            setIsPasswordModalOpen(false)
            setTemporaryPassword(null)
          }}
        />
      )}

      {isDeleteDialogOpen && userToDelete && (
        <DeleteConfirmDialog
          userName={userToDelete.name || userToDelete.email}
          onConfirm={confirmDelete}
          onCancel={() => {
            setIsDeleteDialogOpen(false)
            setUserToDelete(null)
          }}
        />
      )}
    </div>
  )
}

// User Form Modal Component
function UserFormModal({
  user,
  onClose,
  onSuccess,
}: {
  user: UserWithRelations | null
  onClose: () => void
  onSuccess: (temporaryPassword?: string) => void
}) {
  const [doctors, setDoctors] = useState<Doctor[]>([])
  const [patients, setPatients] = useState<Patient[]>([])
  const [formData, setFormData] = useState<CreateUserData>({
    email: user?.email || '',
    name: user?.name || '',
    isActive: user?.isActive ?? true,
    doctorId: user?.doctor?.id || '',
    patientId: user?.patientId || '',
    role: user?.role || 'Recepcionista',
  })
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    loadDoctors()
    loadPatients()
  }, [])

  const loadDoctors = async () => {
    try {
      const data = await doctorsService.getAccessible()
      setDoctors(data)
    } catch (error) {
      console.error('Error loading doctors:', error)
    }
  }

  const loadPatients = async () => {
    try {
      const response = await patientsService.getAll({ page: 1, limit: 1000 })
      setPatients(response.data)
    } catch (error) {
      console.error('Error loading patients:', error)
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setIsSubmitting(true)

    try {
      if (user) {
        const { email, password, ...updateData } = formData
        await usersService.update(user.id, updateData)
        toast.success('Usuário atualizado com sucesso!')
        onSuccess()
      } else {
        const createdUser = await usersService.create(formData)

        // If backend generated a temporary password, show it to the admin
        if (createdUser.temporaryPassword) {
          // Pass the temporary password to the parent
          onSuccess(createdUser.temporaryPassword)
        } else {
          toast.success('Usuário criado com sucesso!')
          onSuccess()
        }
      }
    } catch (error) {
      console.error('Error saving user:', error)
      toast.error(user ? 'Erro ao atualizar usuário' : 'Erro ao criar usuário')
    } finally {
      setIsSubmitting(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            {user ? 'Editar Usuário' : 'Novo Usuário'}
          </h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-mail *</label>
            <input
              type="email"
              required
              disabled={!!user}
              value={formData.email}
              onChange={(e) => setFormData({ ...formData, email: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
              placeholder="usuario@clinica.com"
            />
            {user && <p className="text-xs text-gray-500 mt-1">O e-mail não pode ser alterado</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Nome</label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              placeholder="Nome completo"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">Role *</label>
            <select
              required
              value={formData.role}
              onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            >
              {AVAILABLE_ROLES.map((roleOption) => (
                <option key={roleOption.value} value={roleOption.value}>
                  {roleOption.label}
                </option>
              ))}
            </select>
          </div>

          {formData.role === 'Médico' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Associar com Médico
              </label>
              <select
                value={formData.doctorId}
                onChange={(e) => setFormData({ ...formData, doctorId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Nenhum</option>
                {doctors.map((doctor) => (
                  <option key={doctor.id} value={doctor.id}>
                    {doctor.name} {doctor.crm ? `(${doctor.crm})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          {formData.role === 'Paciente' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Associar com Paciente
              </label>
              <select
                value={formData.patientId}
                onChange={(e) => setFormData({ ...formData, patientId: e.target.value })}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">Nenhum</option>
                {patients.map((patient) => (
                  <option key={patient.id} value={patient.id}>
                    {patient.fullName} {patient.cpf ? `(${patient.cpf})` : ''}
                  </option>
                ))}
              </select>
            </div>
          )}

          <div className="flex items-center gap-4">
            <label className="flex items-center">
              <input
                type="checkbox"
                checked={formData.isActive}
                onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <span className="ml-2 text-sm text-gray-900">Usuário ativo</span>
            </label>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
            >
              {isSubmitting ? 'Salvando...' : user ? 'Atualizar' : 'Criar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  )
}

// Manage Clinics Modal Component
function ManageClinicsModal({
  user,
  onClose,
  onSuccess,
}: {
  user: UserWithRelations
  onClose: () => void
  onSuccess: () => void
}) {
  const [allClinics, setAllClinics] = useState<Clinic[]>([])
  const [userClinicIds, setUserClinicIds] = useState<string[]>(user.clinicIds || [])
  const [isLoading, setIsLoading] = useState(true)

  useEffect(() => {
    loadClinics()
  }, [])

  const loadClinics = async () => {
    try {
      const clinics = await clinicsService.getAccessible()
      setAllClinics(clinics)
    } catch (error) {
      console.error('Error loading clinics:', error)
      toast.error('Erro ao carregar clínicas')
    } finally {
      setIsLoading(false)
    }
  }

  const handleToggleClinic = async (clinicId: string) => {
    try {
      if (userClinicIds.includes(clinicId)) {
        await usersService.removeFromClinic(user.id, clinicId)
        setUserClinicIds(userClinicIds.filter((id) => id !== clinicId))
        toast.success('Usuário removido da clínica')
      } else {
        await usersService.addToClinic(user.id, clinicId)
        setUserClinicIds([...userClinicIds, clinicId])
        toast.success('Usuário adicionado à clínica')
      }
      onSuccess()
    } catch (error) {
      console.error('Error toggling clinic:', error)
      toast.error('Erro ao atualizar associação')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">
            Gerenciar Clínicas - {user.name || user.email}
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Selecione as clínicas às quais este usuário tem acesso
          </p>
        </div>

        <div className="p-6">
          {isLoading ? (
            <div className="text-center py-8 text-gray-500">Carregando...</div>
          ) : (
            <div className="space-y-2">
              {allClinics.map((clinic) => (
                <label
                  key={clinic.id}
                  className="flex items-center p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                >
                  <input
                    type="checkbox"
                    checked={userClinicIds.includes(clinic.id)}
                    onChange={() => handleToggleClinic(clinic.id)}
                    className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                  />
                  <div className="ml-3 flex-1">
                    <div className="text-sm font-medium text-gray-900">{clinic.name}</div>
                    {clinic.address && (
                      <div className="text-sm text-gray-500">{clinic.address}</div>
                    )}
                  </div>
                </label>
              ))}
            </div>
          )}
        </div>

        <div className="p-6 border-t border-gray-200 flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-gray-200 text-gray-800 rounded-lg hover:bg-gray-300"
          >
            Fechar
          </button>
        </div>
      </div>
    </div>
  )
}

// Delete Confirmation Dialog Component
function DeleteConfirmDialog({
  userName,
  onConfirm,
  onCancel,
}: {
  userName: string
  onConfirm: () => void
  onCancel: () => void
}) {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Confirmar Exclusão</h3>
        <p className="text-sm text-gray-600 mb-6">
          Tem certeza que deseja excluir o usuário <strong>{userName}</strong>? Esta ação não pode
          ser desfeita.
        </p>
        <div className="flex justify-end gap-3">
          <button
            onClick={onCancel}
            className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cancelar
          </button>
          <button
            onClick={onConfirm}
            className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
          >
            Excluir
          </button>
        </div>
      </div>
    </div>
  )
}

// Temporary Password Modal Component
function TemporaryPasswordModal({ password, onClose }: { password: string; onClose: () => void }) {
  const [copied, setCopied] = useState(false)

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password)
      setCopied(true)
      toast.success('Senha copiada!')
      setTimeout(() => setCopied(false), 2000)
    } catch (error) {
      toast.error('Erro ao copiar senha')
    }
  }

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6">
        <div className="mb-4">
          <div className="flex items-center justify-center w-12 h-12 rounded-full bg-green-100 mb-4 mx-auto">
            <Key className="text-green-600" size={24} />
          </div>
          <h3 className="text-lg font-semibold text-gray-900 text-center mb-2">
            Senha Temporária Gerada
          </h3>
          <p className="text-sm text-gray-600 text-center mb-4">
            ⚠️ Copie esta senha agora! Ela não será exibida novamente.
          </p>
        </div>

        <div className="bg-gray-50 border-2 border-dashed border-gray-300 rounded-lg p-4 mb-4">
          <div className="flex items-center justify-between">
            <code className="text-lg font-mono font-bold text-gray-900 select-all">{password}</code>
            <button
              onClick={handleCopy}
              className={`ml-3 px-3 py-1 rounded text-sm font-medium transition-colors ${
                copied
                  ? 'bg-green-100 text-green-700'
                  : 'bg-blue-100 text-blue-700 hover:bg-blue-200'
              }`}
            >
              {copied ? '✓ Copiado' : 'Copiar'}
            </button>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
          <p className="text-xs text-amber-800">
            <strong>Importante:</strong> Compartilhe esta senha com o usuário. Ele poderá alterá-la
            na página de perfil quando quiser.
          </p>
        </div>

        <div className="flex justify-end">
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            Entendi
          </button>
        </div>
      </div>
    </div>
  )
}
